import type { RequirementType, RequirementPriorityLevel, RequirementStatus } from '@ispot/shared'

// Tokens reference design-system CSS vars. Critical = danger, High = warning
// (NOT Skerpa accent — accent is reserved for decisive moments). Medium/Low
// recede into warm neutrals so the heat is reserved for what's actually urgent.
export const PRIORITY_TOKENS: Record<RequirementPriorityLevel, { label: string; color: string; bg: string; ink: string; bars: 1 | 2 | 3 }> = {
  critical: { label: 'Critical', color: 'var(--color-danger)',   bg: 'var(--color-danger-bg)',  ink: 'var(--color-danger-fg)',    bars: 3 },
  high:     { label: 'High',     color: 'var(--color-warning)',  bg: 'var(--color-warning-bg)', ink: 'var(--color-warning-fg)',   bars: 3 },
  medium:   { label: 'Medium',   color: 'var(--color-warm-600)', bg: 'var(--color-warm-100)',   ink: 'var(--color-fg-secondary)', bars: 2 },
  low:      { label: 'Low',      color: 'var(--color-warm-500)', bg: 'var(--color-warm-100)',   ink: 'var(--color-fg-muted)',     bars: 1 },
}

// Status tokens use semantic vars. backlog/planned share warm neutrals but
// nudge ink intensity so they read as distinct.
export const STATUS_TOKENS: Record<RequirementStatus, { label: string; color: string; bg: string; ink: string }> = {
  backlog:       { label: 'Backlog',     color: 'var(--color-warm-500)', bg: 'var(--color-warm-100)',   ink: 'var(--color-fg-muted)' },
  planned:       { label: 'Planned',     color: 'var(--color-warm-600)', bg: 'var(--color-warm-100)',   ink: 'var(--color-fg-secondary)' },
  'in-progress': { label: 'In Progress', color: 'var(--color-info)',     bg: 'var(--color-info-bg)',    ink: 'var(--color-info-fg)' },
  'in-review':   { label: 'In Review',   color: 'var(--color-warning)',  bg: 'var(--color-warning-bg)', ink: 'var(--color-warning-fg)' },
  done:          { label: 'Done',        color: 'var(--color-success)',  bg: 'var(--color-success-bg)', ink: 'var(--color-success-fg)' },
}

export const KIND_TOKENS: Record<RequirementType, { label: string; ink: string; glyph: string }> = {
  epic:           { label: 'Epic',         ink: 'var(--color-solution-700)', glyph: '#' },
  story:          { label: 'Story',        ink: 'var(--color-solution-700)', glyph: '•' },
  'nfr-category': { label: 'NFR Category', ink: 'var(--color-solution-700)', glyph: '§' },
}

// ============ Persona portrait ============

const PERSONA_GRADIENTS: Array<{ c1: string; c2: string }> = [
  { c1: '#f4b4a0', c2: '#c76a54' }, // warm coral — Nora
  { c1: '#a6c8e8', c2: '#3d7cc0' }, // blue — Ravi
  { c1: '#c8b3e5', c2: '#7b5cc6' }, // violet — Priya
  { c1: '#a8d6b4', c2: '#4c9670' }, // green — Oskar
  { c1: '#e5c07b', c2: '#a87a1f' }, // amber
  { c1: '#e2a3c4', c2: '#b35a8b' }, // rose
]

function hashString(str: string): number {
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return Math.abs(h)
}

export function personaGradient(name: string): { c1: string; c2: string } {
  return PERSONA_GRADIENTS[hashString(name) % PERSONA_GRADIENTS.length]
}

interface PersonaPortraitProps {
  name: string
  photo?: string
  size?: number
}

/**
 * Persona portrait.
 * - If `photo` is provided (base64 data URL or remote URL), renders the real photo.
 * - Otherwise falls back to a deterministic gradient + silhouette + initial.
 */
export function PersonaPortrait({ name, photo, size = 24 }: PersonaPortraitProps) {
  if (photo) {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: 999,
          flexShrink: 0,
          backgroundImage: `url(${JSON.stringify(photo)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'var(--color-warm-200)',
          boxShadow: 'inset 0 0 0 1px rgba(29,26,21,0.06)',
        }}
        title={name}
      />
    )
  }

  const { c1, c2 } = personaGradient(name)
  const gid = `pg-${hashString(name)}-${size}`
  const initial = name.trim()[0]?.toUpperCase() ?? '?'
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" style={{ borderRadius: 999, display: 'inline-block', flexShrink: 0 }} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c1} />
          <stop offset="100%" stopColor={c2} />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="20" fill={`url(#${gid})`} />
      <circle cx="20" cy="16" r="5.5" fill="#fff" fillOpacity="0.88" />
      <path d="M 9 34 C 9 26, 31 26, 31 34 Z" fill="#fff" fillOpacity="0.88" />
      {size <= 28 && (
        <text x="20" y="25" textAnchor="middle" fontFamily="var(--font-display)" fontSize="16" fontWeight="600" fill={c2}>
          {initial}
        </text>
      )}
    </svg>
  )
}

// ============ Kind badge (for non-story rows) ============

interface KindBadgeProps {
  type: RequirementType
  size?: number
}

export function KindBadge({ type, size = 24 }: KindBadgeProps) {
  const k = KIND_TOKENS[type]
  // Size the glyph proportional to the cell, with a touch larger weight for the
  // bullet (it reads small at the same px size as `#`).
  const glyphSize = type === 'story'
    ? Math.round(size * 0.7)
    : Math.round(size * 0.55)
  return (
    <span
      className="inline-flex items-center justify-center flex-shrink-0 font-mono"
      style={{
        width: size,
        height: size,
        color: k.ink,
        fontSize: glyphSize,
        fontWeight: type === 'story' ? 700 : 500,
        lineHeight: 1,
        userSelect: 'none',
      }}
      aria-label={k.label}
    >
      {k.glyph}
    </span>
  )
}

// ============ Priority cell (3-bar signal) ============

interface PriorityCellProps {
  priority: RequirementPriorityLevel | null
  onClick?: (e: React.MouseEvent) => void
}

export function PriorityCell({ priority, onClick }: PriorityCellProps) {
  if (!priority) {
    return (
      <button
        className="inline-flex items-center gap-1.5 text-[13px] cursor-pointer bg-transparent border-0"
        style={{ color: 'var(--color-warm-400)', fontFamily: 'var(--font-sans)', padding: 0 }}
        onClick={onClick}
      >
        <span className="inline-flex items-end gap-[2px]" style={{ height: 10 }}>
          <span style={{ width: 3, height: 4, background: 'var(--color-border)', borderRadius: 1 }} />
          <span style={{ width: 3, height: 7, background: 'var(--color-border)', borderRadius: 1 }} />
          <span style={{ width: 3, height: 10, background: 'var(--color-border)', borderRadius: 1 }} />
        </span>
        <span>—</span>
      </button>
    )
  }
  const t = PRIORITY_TOKENS[priority]
  const inactiveBar = 'var(--color-border)'
  // Label color stays neutral (--color-fg-secondary). The colored bars carry
  // the priority signal — duplicating it on the text just adds visual noise.
  // Critical is the one exception: bumped to fg + medium weight so it pulls
  // attention without screaming red text.
  const labelColor = priority === 'critical' ? 'var(--color-fg)' : 'var(--color-fg-secondary)'
  return (
    <button
      className="inline-flex items-center gap-1.5 text-[13px] font-medium cursor-pointer bg-transparent border-0"
      style={{ color: labelColor, fontFamily: 'var(--font-sans)', padding: 0 }}
      onClick={onClick}
    >
      <span className="inline-flex items-end gap-[2px]" style={{ height: 10 }}>
        <span style={{ width: 3, height: 4,  background: t.bars >= 1 ? t.color : inactiveBar, borderRadius: 1 }} />
        <span style={{ width: 3, height: 7,  background: t.bars >= 2 ? t.color : inactiveBar, borderRadius: 1 }} />
        <span style={{ width: 3, height: 10, background: t.bars >= 3 ? t.color : inactiveBar, borderRadius: 1 }} />
      </span>
      <span>{t.label}</span>
    </button>
  )
}

// ============ Status chip ============

interface StatusCellProps {
  status: RequirementStatus
  onClick?: (e: React.MouseEvent) => void
}

export function StatusCell({ status, onClick }: StatusCellProps) {
  const t = STATUS_TOKENS[status]
  return (
    <button
      className="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full cursor-pointer border-0 whitespace-nowrap"
      style={{
        background: t.bg,
        color: t.ink,
        padding: '3px 10px',
        fontFamily: 'var(--font-sans)',
        lineHeight: 1.4,
      }}
      onClick={onClick}
    >
      <span style={{ width: 5, height: 5, borderRadius: 999, background: t.color, flexShrink: 0 }} />
      {t.label}
    </button>
  )
}

// ============ Evidence bar ============

interface EvidenceBarProps {
  count: number
  max?: number
}

export function EvidenceBar({ count, max = 8 }: EvidenceBarProps) {
  const pct = Math.min(100, (count / max) * 100)
  const tone = count >= 5 ? 'var(--color-success)' : count >= 2 ? 'var(--color-warning)' : 'var(--color-warm-300)'
  return (
    <span className="inline-flex items-center gap-2" title={`${count} evidence item${count === 1 ? '' : 's'}`}>
      <span
        className="inline-block rounded-full overflow-hidden flex-shrink-0"
        style={{ width: 48, height: 3, background: 'var(--color-warm-200)' }}
      >
        <span className="block h-full" style={{ width: `${pct}%`, background: tone, borderRadius: 999, transition: 'width 150ms cubic-bezier(0.2,0,0,1)' }} />
      </span>
      <span className="text-[12px] tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
        {count}
      </span>
    </span>
  )
}

// ============ Avatar (initial) + stack ============

const AVATAR_GRADIENTS: Record<string, string> = {
  J: 'linear-gradient(135deg, #6aa7e0, #3478c7)',
  S: 'linear-gradient(135deg, #e2a3c4, #b35a8b)',
  M: 'linear-gradient(135deg, #a3d9b1, #4fa073)',
  P: 'linear-gradient(135deg, #e5c07b, #a87a1f)',
  D: 'linear-gradient(135deg, #c8b3e5, #7b5cc6)',
  K: 'linear-gradient(135deg, #f4b4a0, #c76a54)',
  R: 'linear-gradient(135deg, #a6c8e8, #3d7cc0)',
  L: 'linear-gradient(135deg, #a8d6b4, #4c9670)',
}

function initialOf(name: string): string {
  return name.trim()[0]?.toUpperCase() ?? '?'
}

function gradientFor(name: string): string {
  const key = initialOf(name)
  if (AVATAR_GRADIENTS[key]) return AVATAR_GRADIENTS[key]
  const keys = Object.keys(AVATAR_GRADIENTS)
  return AVATAR_GRADIENTS[keys[hashString(name) % keys.length]]
}

interface AvatarProps {
  name: string
  size?: number
  ring?: boolean
}

export function Avatar({ name, size = 22, ring = false }: AvatarProps) {
  return (
    <span
      className="inline-flex items-center justify-center text-white flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: gradientFor(name),
        fontSize: Math.round(size * 0.42),
        fontWeight: 600,
        borderRadius: 999,
        boxShadow: ring ? '0 0 0 1.5px #fff' : undefined,
        fontFamily: 'var(--font-sans)',
      }}
      title={name}
    >
      {initialOf(name)}
    </span>
  )
}

interface AvatarStackProps {
  names: string[]
  size?: number
  max?: number
}

export function AvatarStack({ names, size = 22, max = 3 }: AvatarStackProps) {
  if (names.length === 0) return null
  const shown = names.slice(0, max)
  const overflow = names.length - shown.length
  return (
    <span className="inline-flex items-center">
      {shown.map((name, i) => (
        <span key={`${name}-${i}`} style={{ marginLeft: i === 0 ? 0 : -6, zIndex: 10 - i }}>
          <Avatar name={name} size={size} ring />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center flex-shrink-0"
          style={{
            marginLeft: -6,
            width: size,
            height: size,
            borderRadius: 999,
            background: 'var(--color-warm-200)',
            color: 'var(--color-text-secondary)',
            fontSize: Math.round(size * 0.42),
            fontWeight: 600,
            boxShadow: '0 0 0 1.5px #fff',
            fontFamily: 'var(--font-sans)',
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
